import React, { useEffect } from "react";
import styled from "styled-components";
import { SkeletonLoader, colors2 } from "oolib";
import {
  UI_BODY_SEMIBOLD_SM_DF,
  UI_BODY_SM,
  UI_TAG,
} from "oolib/dist/v2/components/Typo2";
import { DashboardWidgetShell } from "../../comps/DashboardWidgetShell";
import { StatusPill } from "../../comps/StatusPill";
import { useDetailPanel } from "../../comps/ActivePanelContext";
import { useGetTopicsLens, useGetTagDetail } from "../../hooks/useGetLensData";
import { TagDetailSidebar } from "../comps/TagDetailSidebar";
import { WIDGET_STYLE, WIDGET_BODY_STYLE, TOPIC_TREND_THEME } from "../constants";
import { Hash } from "@phosphor-icons/react";

const TopicList = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopicRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem 0;
  border-bottom: 1px solid ${colors2.grey5};
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`;

const TopicNumber = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${colors2.grey10};
  color: ${colors2.grey70};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
`;

const TopicLabel = styled.div`
  flex: 1;
  min-width: 0;
`;

const SignalChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.6rem;
`;

const SignalChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  background: ${colors2.grey5};
  color: ${colors2.grey60};
`;

const TopicRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

export function TopicsLensBlock({ period, locality }) {
  const { selected: selectedTag, select: selectTag, deselect: deselectTag, isOpen: tagPanelOpen } = useDetailPanel("lenses:topics");

  useEffect(() => { deselectTag(); }, [period]);

  const { data, isLoading } = useGetTopicsLens({ period, locality });

  const { data: tagDetail, isLoading: tagDetailLoading } = useGetTagDetail({
    tagId: selectedTag?.tagId,
    period,
    mode: "all",
    locality,
    enabled: !!selectedTag,
  });

  const topics = data?.topics || [];

  return (
    <>
      <DashboardWidgetShell
        titleJSX={
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <Hash size={16} color={colors2.grey80} weight="bold" />
            <UI_BODY_SEMIBOLD_SM_DF>Topics</UI_BODY_SEMIBOLD_SM_DF>
          </div>
        }
        style={WIDGET_STYLE}
        bodyStyle={WIDGET_BODY_STYLE}
      >
        {isLoading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "7px 0" }}>
                <SkeletonLoader style={{ height: 14, width: `${40 + Math.random() * 30}%`, borderRadius: 4 }} />
                <SkeletonLoader style={{ height: 22, width: 60, borderRadius: 12 }} />
              </div>
            ))}
          </div>
        )}
        {!isLoading && topics.length === 0 && (
          <UI_BODY_SM style={{ color: colors2.grey40 }}>No topic data available yet.</UI_BODY_SM>
        )}
        {!isLoading && topics.length > 0 && (
          <TopicList>
            {topics.slice(0, 10).map((topic, idx) => {
              const trendTheme = TOPIC_TREND_THEME[topic.trend] || TOPIC_TREND_THEME.stable;
              return (
                <TopicRow key={topic.tagId} onClick={() => selectTag(topic)}>
                  <TopicNumber>
                    <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey70 }}>{idx + 1}</UI_BODY_SEMIBOLD_SM_DF>
                  </TopicNumber>
                  <TopicLabel>
                    <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey80 }}>
                      {topic.name}
                    </UI_BODY_SEMIBOLD_SM_DF>
                    {topic.signalTags?.length > 0 && (
                      <SignalChipsRow>
                        {topic.signalTags.slice(0, 2).map((sig) => (
                          <SignalChip key={sig}><UI_TAG>{sig}</UI_TAG></SignalChip>
                        ))}
                      </SignalChipsRow>
                    )}
                  </TopicLabel>
                  <TopicRight>
                    <div style={{ minWidth: 60, display: "flex", justifyContent: "center" }}>
                      <UI_BODY_SEMIBOLD_SM_DF style={{ color: colors2.grey60 }}>{topic.currentCount}</UI_BODY_SEMIBOLD_SM_DF>
                    </div>
                    <div style={{ minWidth: 100, display: "flex", justifyContent: "flex-end" }}>
                      <StatusPill theme={trendTheme} />
                    </div>
                  </TopicRight>
                </TopicRow>
              );
            })}
          </TopicList>
        )}
      </DashboardWidgetShell>

      {(() => {
        const tagTheme = TOPIC_TREND_THEME[selectedTag?.trend] || TOPIC_TREND_THEME.stable;
        return (
          <TagDetailSidebar
            isOpen={tagPanelOpen}
            onClose={deselectTag}
            isLoading={tagDetailLoading}
            title={selectedTag?.name}
            mentionsCount={selectedTag?.currentCount}
            description={tagDetail?.description}
            sources={tagDetail?.sources}
            trendData={tagDetail?.trend}
            mentions={(tagDetail?.annotations || []).map((a) => ({ tone: a.sentiment, text: a.text, fragment: a.fragment }))}
            pill={<StatusPill theme={tagTheme} as="span" />}
            primaryColor={tagTheme.accent}
            glowColor={tagTheme.glow}
          />
        );
      })()}
    </>
  );
}
